import React, {
 useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Theme,
  useMediaQuery,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { useTheme } from '@material-ui/styles';
import { useDispatch, useSelector } from 'react-redux';
import useStyles from '../../modal/styles';
import usePhoneDialogStyles from './styles';
import { useId } from '../../../hooks';
import DialogButton from './DialogButton/DialogButton';
import {
  generatePhoneVerificationClear,
  generatePhoneVerificationCodeRequest,
  resetVerificationCodeCurrentAttempt,
  verifyPhoneClear,
  verifyPhoneRequest,
} from '../../../store/modules/phone-verification/actions';
import {
  selectCodeGeneration,
  selectCodeVerification,
} from '../../../store/modules/phone-verification/selectors';
import { PhoneTypes, VerificationCodeRequest } from '../../../types/phoneVerification';
import { SimpleButton, SimpleConfirmationDialog } from '../..';
import { CheckIcon } from '../../icon/solid';
import { getCurrentUserRequest } from '../../../store/modules/users/actions';

type Props = {
  phoneType: PhoneTypes;
  open: boolean;
  onCloseHandler: () => void;
};

const SetupPhoneDialog = ({ open, phoneType, onCloseHandler }: Props) => {
  const classes = useStyles();
  const phoneDialogClasses = usePhoneDialogStyles();
  const id = useId();
  const theme = useTheme<Theme>();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [phone, setPhone] = useState('+1');
  const [code, setCode] = useState('');
  const dispatch = useDispatch();
  const { generated: isCodeGenerated, loading: codeGenerationLoading } = useSelector(selectCodeGeneration);
  const {
    loading: codeVerificationLoading,
    verified: isCodeVerified,
    currentAttempt: currentVerificationAttempt,
  } = useSelector(selectCodeVerification);
  const [openConfirmedPhoneDialog, setOpenConfirmedPhoneDialog] = useState(false);
  const [openRejectedPhoneDialog, setOpenRejectedPhoneDialog] = useState(false);
  const verificationCodeInputRef = useRef<HTMLInputElement>(null);
  const generationCodeInputRef = useRef<HTMLInputElement>(null);
  const generatePhoneCodeRequestHandler = useCallback(() => {
    dispatch(generatePhoneVerificationCodeRequest(phone));
  }, [dispatch, phone]);

  const verifyPhoneCodeRequestHandler = useCallback(() => {
    const payload : VerificationCodeRequest = { code, phone_type: phoneType };
    dispatch(verifyPhoneRequest(payload));
  }, [dispatch, code, phoneType]);

  const onChangePhoneHandler = useCallback((e) => {
    const fullNumber = e.target.value;
    const fullNumberLength = fullNumber.length;
    const firstChar = fullNumber.substr(0, 1);
    const firstCharIsPlus = (firstChar === '+');
    const numberSection = firstCharIsPlus ? fullNumber.substr(1, fullNumberLength) : fullNumber;

    const onlyNums = numberSection.replace(/[^0-9]/g, '');

    const fullNumberChecked = (firstCharIsPlus ? '+' : '') + onlyNums;

    if (fullNumberChecked.length < 12) {
      setPhone(fullNumberChecked);
    } else if (fullNumberChecked.length >= 12) {
      setPhone(fullNumberChecked);
    }
  }, [setPhone]);

  const onChangePhoneCodeHandler = useCallback((e) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, '');
    setCode(onlyNums);
  }, [setCode]);

  // @ts-ignore
  const clearStateHandler = useCallback((resetPhone: boolean = true) => {
    setPhone(resetPhone ? '+1' : phone);
    setCode('');
    dispatch(verifyPhoneClear());
    dispatch(generatePhoneVerificationClear());
    dispatch(resetVerificationCodeCurrentAttempt());
    if (generationCodeInputRef?.current) {
      generationCodeInputRef.current.focus();
    }
  }, [setPhone, phone, setCode, dispatch, generationCodeInputRef]);

  const cancelVerificationHandler = useCallback(() => {
    clearStateHandler();
    onCloseHandler();
  }, [onCloseHandler, clearStateHandler]);

  useEffect(() => {
    if (isCodeVerified) {
      clearStateHandler();
      onCloseHandler();
      setOpenConfirmedPhoneDialog(true);
    }
  }, [isCodeVerified, clearStateHandler, onCloseHandler, setOpenConfirmedPhoneDialog]);

  useEffect(() => {
    if (currentVerificationAttempt > 0) {
      setCode('');

      if (verificationCodeInputRef?.current) {
        verificationCodeInputRef.current.focus();
      }
    }

    if (currentVerificationAttempt >= 3) {
      clearStateHandler();
      onCloseHandler();
      setOpenRejectedPhoneDialog(true);
    }
  }, [currentVerificationAttempt, clearStateHandler, onCloseHandler, verificationCodeInputRef]);

  const enterHandler = useCallback(() => {
    if (!isCodeGenerated) {
      if (!codeGenerationLoading) generatePhoneCodeRequestHandler();
    } else {
      if (!codeVerificationLoading) verifyPhoneCodeRequestHandler();
    }
  }, [
    isCodeGenerated,
    generatePhoneCodeRequestHandler,
    verifyPhoneCodeRequestHandler,
    codeGenerationLoading,
    codeVerificationLoading,
  ]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'Enter':
        enterHandler();
        break;
      case 'Escape':
        cancelVerificationHandler();
        break;
      default:
        break;
    }
  }, [enterHandler, cancelVerificationHandler]);

  useEffect(() => {
    if (isCodeGenerated && verificationCodeInputRef?.current) {
      verificationCodeInputRef.current.focus();
    }
  }, [isCodeGenerated, verificationCodeInputRef]);

  return (
    <>
      <Dialog
        open={open}
        aria-labelledby={id}
        disableBackdropClick
        PaperProps={{ className: classes.modal }}
        fullWidth
        fullScreen={fullScreen}
        style={{ zIndex: 2147483100 }}
        onKeyDown={handleKeyDown}
      >
        <DialogTitle className={phoneDialogClasses.dialogHeader} id={id}>
          <div className={phoneDialogClasses.titleWrapper}>
            <span className={phoneDialogClasses.title}>
              { !isCodeGenerated ? 'Enter your phone number' : 'Enter verification code'}
            </span>
            <IconButton aria-label="close" className={phoneDialogClasses.closeButton} onClick={cancelVerificationHandler}>
              <CloseIcon className={phoneDialogClasses.closeIcon} />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className={phoneDialogClasses.dialogContentWrapper}>
          <div className={phoneDialogClasses.contentMessage}>
            {
              !isCodeGenerated
                ? (
                  <>
                    <p>Please use + and the country code</p>
                    <p>e.g. U.S. numbers start with +1</p>
                  </>
                )
                : `We have just sent a text message to ${phone}
                with a one time verification code. It may take a few minutes.`
            }
          </div>
          <div>
            { !isCodeGenerated
              ? (
                <div>
                  <TextField
                    inputRef={verificationCodeInputRef}
                    type="text"
                    label="Phone number"
                    id="phone-number-input"
                    value={phone}
                    onChange={onChangePhoneHandler}
                    autoFocus
                    InputLabelProps={{
                      classes: {
                        root: phoneDialogClasses.labelRoot,
                        focused: phoneDialogClasses.focused,
                      },
                    }}
                    InputProps={{
                      classes: {
                        underline: phoneDialogClasses.inputUnderline,
                        focused: phoneDialogClasses.focused,
                        disabled: phoneDialogClasses.disabled,
                      },
                    }}
                  />
                  <div className={phoneDialogClasses.contentMessage}>
                    <p>We will be sending you a code to verify your phone number.</p>
                    <p>Standard text and data rates may apply.</p>
                  </div>
                </div>
              )
              : (
                <>
                  <div>
                    <TextField
                      inputRef={generationCodeInputRef}
                      className={classes.input}
                      label="6-digit code"
                      id="digit-code-input"
                      InputLabelProps={{
                        classes: {
                          root: phoneDialogClasses.labelRoot,
                          focused: phoneDialogClasses.focused,
                        },
                      }}
                      inputProps={{ maxLength: 6 }}
                      InputProps={{
                        classes: {
                          underline: phoneDialogClasses.inputUnderline,
                          focused: phoneDialogClasses.focused,
                          disabled: phoneDialogClasses.disabled,
                        },
                      }}
                      value={code}
                      onChange={onChangePhoneCodeHandler}
                      autoFocus
                      error={!!currentVerificationAttempt}
                      helperText={currentVerificationAttempt
                        ? `The code is not valid (${currentVerificationAttempt} of 3 attempts)`
                        : null}
                    />
                  </div>
                  <div>
                    <SimpleButton onClick={() => clearStateHandler(false)}>Send code again</SimpleButton>
                  </div>
                </>
              )}
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <DialogButton type="cancel" onClick={cancelVerificationHandler} />

          {!isCodeGenerated
            ? (
              <DialogButton
                disabled={codeGenerationLoading || !(phone && phone.length >= 12)}
                onClick={generatePhoneCodeRequestHandler}
                title="Send code"
              />
            )
            : (
              <DialogButton
                title={(
                  <>
                    <CheckIcon className={classes.buttonIcon} />
                    Verify
                  </>
                )}
                disabled={codeVerificationLoading || !(code && code.length === 6)}
                onClick={verifyPhoneCodeRequestHandler}
              />
            )}
        </DialogActions>
      </Dialog>
      <SimpleConfirmationDialog
        title="Your mobile phone number has not been verified"
        message={phoneType === 'sms_phone' ? 'Please make sure your phone number is correct.' : ''}
        open={openRejectedPhoneDialog}
        closeHandler={() => setOpenRejectedPhoneDialog(false)}
      />
      <SimpleConfirmationDialog
        title="Your mobile phone number has been confirmed"
        message={phoneType === 'sms_phone' ? 'You can now receive text notifications.' : ''}
        open={openConfirmedPhoneDialog}
        closeHandler={() => { setOpenConfirmedPhoneDialog(false); dispatch(getCurrentUserRequest()); }}
      />
    </>
  );
};

export default SetupPhoneDialog;
